/**
 * @file CatalogPanel.tsx
 * @description Панель «Каталог» (phase19.16 Р2а; uiux-design-r2a §2, §4,
 * §5; db-design-r2a, ответ 1; SB31, SB61, SB62, SB65): вкладки [Список]
 * [Карточка товара]. Часть CATALOG черновика вида сайта — автосохранение
 * (`useSiteDraftPart("catalog")`, `PATCH draft/catalog { options }`), на
 * сайт — вместе с видом сайта («Не опубликовано: каталог N»). Блоки над и
 * под списком — строкой со ссылкой на экран «Каталог» (Р3а: через черновик места).
 * Канва показывает каталог; правка уходит живым сообщением
 * `commora:catalog-options-preview`, а после сохранения канва
 * перерисовывается (если витрина сообщение не знает).
 */

import { readCatalogOptions, type SiteCatalogOptions } from "@chaiberi/shared";
import { useContext, useEffect, useMemo, useState } from "react";

import { Tabs } from "../../ui/Tabs";
import { BuilderContext } from "../../site-builder/builder-context";
import { SaveIndicator } from "../SaveIndicator";
import { useSiteView } from "../site-view-context";
import { UnsavedGuard } from "../UnsavedGuard";
import { CatalogCardTab } from "./CatalogCardTab";
import { CatalogListTab } from "./CatalogListTab";
import { CatalogSlotsLine } from "./CatalogSlotsLine";
import { useSiteDraftPart } from "../../../hooks/site-view/useSiteDraftParts";
import { catalogOptionsMessage, postToCanvas } from "../../../lib/site-view/canvas-messages";
import type { CardTemplateMemory } from "../../../lib/site-view/catalog-form";

export type CatalogTab = "list" | "card";

const TABS = [
  { value: "list" as const, label: "Список" },
  { value: "card" as const, label: "Карточка товара" },
];

interface Props {
  /** Вкладка при открытии (из адреса `panel=catalog.card`). */
  initialTab?: CatalogTab;
}

export function CatalogPanel({ initialTab = "list" }: Props) {
  const view = useSiteView();
  const catalog = useSiteDraftPart("catalog");
  const iframeRef = useContext(BuilderContext)?.iframeRef ?? null;
  const [tab, setTab] = useState<CatalogTab>(initialTab);
  // Что шаблон карточки поменял сам — живёт, пока открыта панель (между вкладками тоже).
  const [memory, setMemory] = useState<CardTemplateMemory>({});
  const raw = catalog.fields.options;
  const options = useMemo(() => readCatalogOptions(raw).options, [raw]);
  const setOptions = (next: SiteCatalogOptions) => catalog.set("options", next);
  const { focus } = view.preview;
  const colors = view.parts?.theme.theme.colors ?? null;
  const shapes = view.parts?.theme.theme.shapes ?? null;

  useEffect(() => {
    focus({ screen: "catalog" });
  }, [focus]);
  // Живой вид на канве — до сохранения.
  useEffect(() => {
    if (iframeRef) postToCanvas(iframeRef.current, catalogOptionsMessage(options));
  }, [iframeRef, options]);

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <p className="type-caption text-on-surface-variant">Как выглядит список товаров и карточка товара на сайте. Видно покупателям после «Опубликовать».</p>
      <SaveIndicator state={catalog} />
      <div className="shrink-0">
        <Tabs aria-label="Разделы каталога" items={TABS} value={tab} onChange={setTab} />
      </div>
      {tab === "list" ? (
        <>
          <CatalogSlotsLine />
          <CatalogListTab options={options} onChange={setOptions} />
        </>
      ) : (
        <CatalogCardTab options={options} onChange={setOptions} colors={colors} shapes={shapes} memory={memory} onMemory={setMemory} />
      )}
      <UnsavedGuard when={catalog.status === "error" || catalog.status === "conflict"} />
    </div>
  );
}
